import { Phone, Mail } from "lucide-react";
import { LogoMark } from "./Logo";
import { SITE, TEL_HREF, MAIL_HREF, trackEvent } from "@/siteConfig";

export const Footer = () => (
  <footer
    data-testid="site-footer"
    className="border-t border-white/[0.08] bg-[#080B0F] pb-32 pt-14 md:pb-14"
  >
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 md:grid-cols-4">
        <div>
          <div className="flex items-center gap-3">
            <LogoMark className="h-9 w-9" />
            <span className="flex items-baseline gap-1.5 font-display font-extrabold tracking-tight">
              <span className="text-lg text-zinc-100">FIX&nbsp;ROMA</span>
              <span className="rounded-md bg-amber-400 px-1.5 py-0.5 font-tech text-[11px] font-semibold text-zinc-950">
                H24
              </span>
            </span>
          </div>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-zinc-500">
            Pronto intervento H24 a Roma e provincia per cancelli, serrande,
            automazioni, serrature e interventi elettrici urgenti, anche di
            notte.
          </p>
        </div>
        <div>
          <p className="font-tech text-[11px] uppercase tracking-[0.25em] text-amber-400">
            Servizi
          </p>
          <ul className="mt-4 space-y-2.5 text-sm text-zinc-400">
            <li><a href="/pronto-intervento-cancelli-roma" data-testid="footer-link-cancelli" className="transition-colors hover:text-amber-300">Cancelli</a></li>
            <li><a href="/pronto-intervento-serrande-roma" data-testid="footer-link-serrande" className="transition-colors hover:text-amber-300">Serrande</a></li>
            <li><a href="/automazioni-cancelli-roma" data-testid="footer-link-automazioni" className="transition-colors hover:text-amber-300">Automazioni</a></li>
            <li><a href="/pronto-intervento-serrature-roma" data-testid="footer-link-serrature" className="transition-colors hover:text-amber-300">Serrature e accessi</a></li>
            <li><a href="/apertura-porte-roma" data-testid="footer-link-apertura-porte" className="transition-colors hover:text-amber-300">Apertura porte</a></li>
            <li><a href="/elettricista-pronto-intervento-roma" data-testid="footer-link-elettrico" className="transition-colors hover:text-amber-300">Pronto intervento elettrico</a></li>
          </ul>
        </div>
        <div>
          <p className="font-tech text-[11px] uppercase tracking-[0.25em] text-amber-400">
            Urgenze
          </p>
          <ul className="mt-4 space-y-2.5 text-sm text-zinc-400">
            <li><a href="/pronto-intervento-roma" data-testid="footer-link-pronto-intervento" className="transition-colors hover:text-amber-300">Pronto intervento Roma</a></li>
            <li><a href="/pronto-intervento-notturno-roma" data-testid="footer-link-notturno" className="transition-colors hover:text-amber-300">Pronto intervento notturno</a></li>
            <li><a href="/cancello-automatico-bloccato-roma" data-testid="footer-link-cancello-bloccato" className="transition-colors hover:text-amber-300">Cancello automatico bloccato</a></li>
            <li><a href="/serranda-elettrica-bloccata-roma" data-testid="footer-link-serranda-bloccata" className="transition-colors hover:text-amber-300">Serranda elettrica bloccata</a></li>
            <li><a href="/fabbro-pronto-intervento-roma" data-testid="footer-link-fabbro" className="transition-colors hover:text-amber-300">Fabbro pronto intervento</a></li>
            <li><a href="/porta-blindata-bloccata-roma" data-testid="footer-link-porta-blindata" className="transition-colors hover:text-amber-300">Porta blindata bloccata</a></li>
            <li><a href="/elettricista-h24-roma" data-testid="footer-link-elettricista-h24" className="transition-colors hover:text-amber-300">Elettricista H24</a></li>
            <li><a href="/salvavita-scatta-roma" data-testid="footer-link-salvavita" className="transition-colors hover:text-amber-300">Salvavita che scatta</a></li>
            <li><a href="/guasto-elettrico-roma" data-testid="footer-link-guasto" className="transition-colors hover:text-amber-300">Guasto elettrico urgente</a></li>
            <li><a href="/riparazione-cancelli-roma" data-testid="footer-link-riparazione" className="transition-colors hover:text-amber-300">Riparazione cancelli</a></li>
          </ul>
        </div>
        <div>
          <p className="font-tech text-[11px] uppercase tracking-[0.25em] text-amber-400">
            Contatti
          </p>
          <ul className="mt-4 space-y-3 text-sm">
            <li>
              <a
                href={TEL_HREF}
                data-testid="footer-phone-link"
                className="flex items-center gap-2.5 text-zinc-300 transition-colors hover:text-amber-300"
              >
                <Phone className="h-4 w-4 text-amber-400" /> {SITE.phoneDisplay}
              </a>
            </li>
            <li>
              <a
                href={MAIL_HREF}
                data-testid="footer-email-link"
                onClick={() => trackEvent("click_email", { cta_location: "footer" })}
                className="flex items-center gap-2.5 break-all text-zinc-300 transition-colors hover:text-amber-300"
              >
                <Mail className="h-4 w-4 shrink-0 text-amber-400" /> {SITE.email}
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="mt-12 border-t border-white/[0.06] pt-6">
        <p className="text-xs leading-relaxed text-zinc-600">
          © {new Date().getFullYear()} {SITE.name} — Via di Torre Morena 92,
          00118 Roma (RM) · P.IVA 13434831007.
        </p>
        <p className="mt-2 text-xs text-zinc-600">
          <a
            href="/privacy-policy"
            data-testid="footer-privacy-link"
            className="underline-offset-2 transition-colors hover:text-amber-300 hover:underline"
          >
            Privacy Policy
          </a>
          <span className="mx-2 text-zinc-700">·</span>
          <a
            href="/cookie-policy"
            data-testid="footer-cookie-link"
            className="underline-offset-2 transition-colors hover:text-amber-300 hover:underline"
          >
            Cookie Policy
          </a>
        </p>
      </div>
    </div>
  </footer>
);
